import type { AppRole } from '../../shared/roles';
import { HomeGlance } from '../components/HomeGlance';
import { WorkTiles } from '../components/WorkTiles';
import { useEffect, useState } from 'preact/hooks';
import type { ReviewItem, ReviewQueueBody } from '../../shared/pr/api';
import { isOpenForRequester, waitsOnRequester } from '../../shared/pr/progress';
import { RequestRow } from '../components/RequestRow';
import { useI18n } from '../i18n';
import { apiGet } from '../lib/api';
import { useMyRequests, type ListState } from '../lib/requests';

// Home (mockup 01): the requester's numbers, what waits on them, and their latest requests.

export function LoadProblem({ state }: { state: Extract<ListState, { kind: 'error' }> }) {
  const { t } = useI18n();
  return (
    <div role="alert">
      <p>{t('req.loadError')}</p>
      {state.reference && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: state.reference })}
        </p>
      )}
    </div>
  );
}

const STAFF: readonly AppRole[] = ['Procurement', 'CFO', 'Finance'];
// Who may read the dashboard (01-SPEC §7); department heads get their own departments.
const DASHBOARD: readonly AppRole[] = ['Admin', 'Procurement', 'MgmtViewer', 'CFO'];

export function HomePage({ roles = [] }: { roles?: readonly AppRole[] }) {
  const { t } = useI18n();
  const state = useMyRequests();
  const [toDecide, setToDecide] = useState<ReviewItem[]>([]);
  const [headOf, setHeadOf] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    // Department heads: requests Procurement sent them (P1.5). Empty for everyone else.
    apiGet<ReviewQueueBody>('/api/prs/for-decision')
      .then((b) => {
        if (!live) return;
        setToDecide(b.items);
        setHeadOf(b.headOf ?? []);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const requests = state.kind === 'ok' ? state.requests : [];
  const waiting = requests.filter((r) => waitsOnRequester(r.status));
  const tiles = [
    {
      key: 'open',
      label: t('home.tile.open'),
      value: requests.filter((r) => isOpenForRequester(r.status)).length,
    },
    { key: 'waiting', label: t('home.tile.waiting'), value: waiting.length + toDecide.length },
    {
      key: 'drafts',
      label: t('home.tile.drafts'),
      value: requests.filter((r) => r.status === 'Draft').length,
    },
  ];

  return (
    <div class="stack">
      <div class="actions">
        <a class="button button--primary" href="#/requests/new">
          + {t('home.newRequest')}
        </a>
        {headOf.length > 0 && (
          <a class="button" href="#/dashboard">
            {t('home.headDashboard')}
          </a>
        )}
      </div>
      {(DASHBOARD.some((r) => roles.includes(r)) || headOf.length > 0) && <HomeGlance />}
      <WorkTiles roles={roles} />
      {state.kind === 'ok' && STAFF.some((r) => roles.includes(r)) && (
        <h2 class="card__title">{t('nav.myRequests')}</h2>
      )}
      {state.kind === 'ok' && (
        <ul class="tiles" aria-label={t('home.tiles')}>
          {tiles.map((tile) => (
            <li
              key={tile.key}
              class={`tile tile--tone tile--tone-${tile.key}${tile.key === 'waiting' && tile.value > 0 ? ' tile--warning' : ''}`}
            >
              <span class="caption">{tile.label}</span>
              <span class="tile__value">{tile.value}</span>
            </li>
          ))}
        </ul>
      )}
      <div class="home-layout">
        <div class="stack">
          {toDecide.length > 0 && (
            <section class="card" aria-labelledby="home-decide">
              <h2 id="home-decide" class="card__title">
                {t('home.decide.title')}
              </h2>
              <ul class="action-list">
                {toDecide.map((r) => (
                  <li key={r.prNo} class="notice notice--action">
                    <div>
                      <p class="notice__lead">
                        {r.title} · {r.requesterName}
                      </p>
                      <p class="caption ref" dir="ltr">
                        {r.prNo}
                      </p>
                    </div>
                    <a class="button button--primary" href={`#/requests/${r.prNo}`}>
                      {t('home.decide.open')}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {waiting.length > 0 && (
            <section class="card" aria-labelledby="home-action">
              <h2 id="home-action" class="card__title">
                {t('home.action.title')}
              </h2>
              <ul class="action-list">
                {waiting.map((r) => (
                  <li key={r.prNo} class="notice notice--action">
                    <div>
                      <p class="notice__lead">
                        {r.status === 'Draft' ? t('home.action.draft') : t('home.action.returned')}
                        {' · '}
                        {r.title}
                      </p>
                      <p class="caption ref" dir="ltr">
                        {r.prNo}
                      </p>
                    </div>
                    <a
                      class="button button--primary"
                      href={
                        r.status === 'Draft' ? `#/requests/draft/${r.prNo}` : `#/requests/${r.prNo}`
                      }
                    >
                      {r.status === 'Draft' ? t('home.action.continue') : t('home.action.open')}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section class="card" aria-labelledby="home-recent">
            <div class="card__head">
              <h2 id="home-recent" class="card__title">
                {t('home.recent')}
              </h2>
              {requests.length > 0 && <a href="#/requests">{t('home.seeAll')}</a>}
            </div>
            {state.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
            {state.kind === 'error' && <LoadProblem state={state} />}
            {state.kind === 'ok' &&
              (requests.length === 0 ? (
                <p>{t('req.none')}</p>
              ) : (
                <ul class="req-list">
                  {requests.slice(0, 5).map((r) => (
                    <RequestRow key={r.prNo} pr={r} />
                  ))}
                </ul>
              ))}
          </section>
        </div>
        <aside class="card card--info" aria-labelledby="home-how">
          <h2 id="home-how" class="card__title">
            {t('home.how.title')}
          </h2>
          <ol class="steps-list">
            {(['home.how.1', 'home.how.2', 'home.how.3', 'home.how.4'] as const).map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
          </ol>
        </aside>
      </div>
    </div>
  );
}
